import { useState, type FormEvent, type ReactNode } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { GraduationCap, Loader2, Pencil, Plus } from 'lucide-react'
import { ApiError } from '@/lib/api-client'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { EmptyState } from '@/components/ui/empty-state'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  createDocente,
  listDocentes,
  reactivateDocente,
  suspendDocente,
  updateDocente,
  type Docente,
  type DocenteStatusFilter,
} from '@/features/docentes/api'
import { docentesCopy } from '@/features/docentes/copy'

function formatDate(iso: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('es-UY', { day: '2-digit', month: 'short', year: 'numeric' })
}

type PendingAction = { docente: Docente; action: 'suspend' | 'reactivate' }
type FormTarget = { mode: 'create' } | { mode: 'edit'; docente: Docente }

// Traduce el codigo de error de la API a copy de formulario.
function formErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof ApiError) {
    if (err.code === 'EMAIL_TAKEN') return docentesCopy.form.emailTaken
  }
  return err instanceof Error && err.message ? err.message : fallback
}

export function DocentesListPage(): ReactNode {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<DocenteStatusFilter>('all')
  const [pending, setPending] = useState<PendingAction | null>(null)
  const [formTarget, setFormTarget] = useState<FormTarget | null>(null)

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['docentes', 'list', { status: statusFilter }],
    queryFn: () => listDocentes(statusFilter),
  })

  const items = data ?? []
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['docentes'] })

  const suspendMutation = useMutation({
    mutationFn: ({ docente, action }: PendingAction) =>
      action === 'suspend' ? suspendDocente(docente.id) : reactivateDocente(docente.id),
    onSuccess: (_res, vars) => {
      toast.success(
        vars.action === 'suspend'
          ? docentesCopy.toasts.suspended
          : docentesCopy.toasts.reactivated,
      )
      void invalidate()
    },
    onError: (err: unknown, vars) => {
      const fallback =
        vars.action === 'suspend'
          ? docentesCopy.toasts.suspendError
          : docentesCopy.toasts.reactivateError
      toast.error(err instanceof Error ? err.message : fallback)
    },
    onSettled: () => setPending(null),
  })

  // Toggle inline del permiso de publicar (reusa updateDocente).
  const publishMutation = useMutation({
    mutationFn: ({ id, canPublishFichas }: { id: string; canPublishFichas: boolean }) =>
      updateDocente(id, { canPublishFichas }),
    onSuccess: (updated) => {
      toast.success(
        updated.canPublishFichas
          ? docentesCopy.publish.grantedToast
          : docentesCopy.publish.revokedToast,
      )
      void invalidate()
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : docentesCopy.publish.error)
    },
  })

  const fullName = (d: Docente): string => `${d.nombre} ${d.apellido}`.trim() || d.email

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{docentesCopy.sectionTitle}</h1>
          <p className="text-sm text-muted-foreground">{docentesCopy.sectionDescription}</p>
        </div>
        <Button onClick={() => setFormTarget({ mode: 'create' })}>
          <Plus className="h-4 w-4" />
          {docentesCopy.newDocente}
        </Button>
      </div>

      <div className="grid grid-cols-1 gap-3 rounded-lg border bg-card p-4 sm:max-w-xs">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="status">{docentesCopy.filters.status}</Label>
          <Select
            id="status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as DocenteStatusFilter)}
          >
            <option value="all">{docentesCopy.filters.all}</option>
            <option value="active">{docentesCopy.filters.active}</option>
            <option value="suspended">{docentesCopy.filters.suspended}</option>
          </Select>
        </div>
      </div>

      <div className="rounded-lg border bg-card">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{docentesCopy.columns.nombre}</TableHead>
              <TableHead>{docentesCopy.columns.apellido}</TableHead>
              <TableHead>{docentesCopy.columns.email}</TableHead>
              <TableHead>{docentesCopy.columns.username}</TableHead>
              <TableHead className="w-[140px]">{docentesCopy.columns.alta}</TableHead>
              <TableHead className="w-[120px]">{docentesCopy.columns.estado}</TableHead>
              <TableHead className="w-[140px]">{docentesCopy.columns.publicar}</TableHead>
              <TableHead className="w-[180px] text-right">{docentesCopy.columns.acciones}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((d) => {
              const suspended = d.suspendedAt !== null
              return (
                <TableRow key={d.id}>
                  <TableCell className="font-medium">{d.nombre}</TableCell>
                  <TableCell>{d.apellido}</TableCell>
                  <TableCell className="text-muted-foreground">{d.email}</TableCell>
                  <TableCell className="text-muted-foreground">{d.username ?? '—'}</TableCell>
                  <TableCell>{formatDate(d.createdAt)}</TableCell>
                  <TableCell>
                    {suspended ? (
                      <Badge variant="warning">{docentesCopy.status.suspended}</Badge>
                    ) : (
                      <Badge variant="success">{docentesCopy.status.active}</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={d.canPublishFichas}
                        disabled={publishMutation.isPending}
                        aria-label={docentesCopy.publish.toggleAria(fullName(d))}
                        onChange={(e) =>
                          publishMutation.mutate({ id: d.id, canPublishFichas: e.target.checked })
                        }
                      />
                      <span className="text-sm text-muted-foreground">
                        {d.canPublishFichas ? docentesCopy.publish.on : docentesCopy.publish.off}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center justify-end gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        aria-label={docentesCopy.actions.edit}
                        onClick={() => setFormTarget({ mode: 'edit', docente: d })}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      {suspended ? (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPending({ docente: d, action: 'reactivate' })}
                        >
                          {docentesCopy.actions.reactivate}
                        </Button>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setPending({ docente: d, action: 'suspend' })}
                        >
                          {docentesCopy.actions.suspend}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>

        <div className="border-t p-6">
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Cargando...</p>
          ) : isError ? (
            <p className="text-sm text-destructive">
              {error instanceof Error ? error.message : docentesCopy.toasts.loadError}
            </p>
          ) : items.length === 0 ? (
            <EmptyState
              icon={<GraduationCap className="h-8 w-8" />}
              title={docentesCopy.empty.title}
              description={docentesCopy.empty.description}
              action={
                <Button onClick={() => setFormTarget({ mode: 'create' })}>
                  <Plus className="h-4 w-4" />
                  {docentesCopy.newDocente}
                </Button>
              }
            />
          ) : null}
        </div>
      </div>

      <DocenteFormDialog
        target={formTarget}
        onClose={() => setFormTarget(null)}
        onSaved={() => {
          setFormTarget(null)
          void invalidate()
        }}
      />

      <Dialog
        open={pending !== null}
        onOpenChange={(open) => {
          if (!open) setPending(null)
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {pending?.action === 'suspend'
                ? docentesCopy.suspendDialog.title
                : docentesCopy.reactivateDialog.title}
            </DialogTitle>
            <DialogDescription>
              {pending
                ? pending.action === 'suspend'
                  ? docentesCopy.suspendDialog.confirm(fullName(pending.docente))
                  : docentesCopy.reactivateDialog.confirm(fullName(pending.docente))
                : null}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setPending(null)}
              disabled={suspendMutation.isPending}
            >
              {docentesCopy.actions.cancel}
            </Button>
            <Button
              variant={pending?.action === 'suspend' ? 'destructive' : 'default'}
              disabled={suspendMutation.isPending}
              onClick={() => {
                if (pending) suspendMutation.mutate(pending)
              }}
            >
              {suspendMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              {pending?.action === 'suspend'
                ? docentesCopy.actions.suspend
                : docentesCopy.actions.reactivate}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

interface DocenteFormDialogProps {
  target: FormTarget | null
  onClose: () => void
  onSaved: () => void
}

function DocenteFormDialog({ target, onClose, onSaved }: DocenteFormDialogProps): ReactNode {
  return (
    <Dialog
      open={target !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent>
        {/* key remonta el form al cambiar de target, reseteando el estado local. */}
        {target ? (
          <DocenteForm
            key={target.mode === 'edit' ? target.docente.id : 'create'}
            target={target}
            onCancel={onClose}
            onSaved={onSaved}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

interface DocenteFormProps {
  target: FormTarget
  onCancel: () => void
  onSaved: () => void
}

function DocenteForm({ target, onCancel, onSaved }: DocenteFormProps): ReactNode {
  const isEdit = target.mode === 'edit'
  const initial = isEdit ? target.docente : null

  const [nombre, setNombre] = useState(initial?.nombre ?? '')
  const [apellido, setApellido] = useState(initial?.apellido ?? '')
  const [email, setEmail] = useState(initial?.email ?? '')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [canPublish, setCanPublish] = useState(initial?.canPublishFichas ?? false)
  const [localError, setLocalError] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        // Solo enviamos lo que cambio; password vacio = no cambiar.
        const patch: {
          nombre?: string
          apellido?: string
          email?: string
          password?: string
          canPublishFichas?: boolean
        } = {}
        if (nombre.trim() !== initial!.nombre) patch.nombre = nombre.trim()
        if (apellido.trim() !== initial!.apellido) patch.apellido = apellido.trim()
        if (email.trim() !== initial!.email) patch.email = email.trim()
        if (password) patch.password = password
        if (canPublish !== initial!.canPublishFichas) patch.canPublishFichas = canPublish
        if (Object.keys(patch).length === 0) return null
        return updateDocente(initial!.id, patch)
      }
      return createDocente({
        nombre: nombre.trim(),
        apellido: apellido.trim(),
        email: email.trim(),
        password,
        canPublishFichas: canPublish,
      })
    },
    onSuccess: () => {
      toast.success(isEdit ? docentesCopy.form.updatedToast : docentesCopy.form.createdToast)
      onSaved()
    },
    onError: (err: unknown) => {
      setLocalError(
        formErrorMessage(err, isEdit ? docentesCopy.form.updateError : docentesCopy.form.createError),
      )
    },
  })

  const handleSubmit = (ev: FormEvent) => {
    ev.preventDefault()
    setLocalError(null)
    if (!nombre.trim() || !apellido.trim() || !email.trim()) {
      setLocalError(docentesCopy.form.required)
      return
    }
    // Password: obligatorio al crear; opcional al editar, pero si se ingresa >= 8.
    if (!isEdit && password.length < 8) {
      setLocalError(docentesCopy.form.passwordTooShort)
      return
    }
    if (isEdit && password.length > 0 && password.length < 8) {
      setLocalError(docentesCopy.form.passwordTooShort)
      return
    }
    // Confirmación: solo aplica cuando hay una contraseña que setear (siempre al
    // crear; al editar únicamente si el admin ingresó una nueva).
    if (password.length > 0 && password !== confirmPassword) {
      setLocalError(docentesCopy.form.passwordMismatch)
      return
    }
    mutation.mutate()
  }

  return (
    <form onSubmit={handleSubmit} className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>
          {isEdit ? docentesCopy.form.editTitle : docentesCopy.form.createTitle}
        </DialogTitle>
        <DialogDescription>
          {isEdit ? docentesCopy.form.editDescription : docentesCopy.form.createDescription}
        </DialogDescription>
      </DialogHeader>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="nombre">{docentesCopy.form.nombre}</Label>
          <Input id="nombre" value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="off" />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="apellido">{docentesCopy.form.apellido}</Label>
          <Input id="apellido" value={apellido} onChange={(e) => setApellido(e.target.value)} autoComplete="off" />
        </div>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="email">{docentesCopy.form.email}</Label>
        <Input
          id="email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="off"
        />
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="password">{docentesCopy.form.password}</Label>
        <Input
          id="password"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="new-password"
          placeholder={isEdit ? '••••••••' : ''}
        />
        <p className="text-xs text-muted-foreground">
          {isEdit ? docentesCopy.form.passwordEditHint : docentesCopy.form.passwordHint}
        </p>
      </div>

      <div className="flex flex-col gap-1.5">
        <Label htmlFor="confirmPassword">{docentesCopy.form.passwordConfirm}</Label>
        <Input
          id="confirmPassword"
          type="password"
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          autoComplete="new-password"
          placeholder={isEdit ? '••••••••' : ''}
        />
      </div>

      <div className="flex items-start justify-between gap-4 rounded-lg border p-3">
        <div className="flex flex-col gap-0.5">
          <Label htmlFor="canPublish">{docentesCopy.form.canPublish}</Label>
          <p className="text-xs text-muted-foreground">{docentesCopy.form.canPublishHint}</p>
        </div>
        <Switch
          id="canPublish"
          checked={canPublish}
          onChange={(e) => setCanPublish(e.target.checked)}
        />
      </div>

      {localError ? <p className="text-sm text-destructive">{localError}</p> : null}

      <DialogFooter>
        <Button type="button" variant="outline" onClick={onCancel} disabled={mutation.isPending}>
          {docentesCopy.actions.cancel}
        </Button>
        <Button type="submit" disabled={mutation.isPending}>
          {mutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
          {isEdit ? docentesCopy.actions.save : docentesCopy.actions.create}
        </Button>
      </DialogFooter>
    </form>
  )
}
